import {StorageHelper} from "../../helpers/storage.helper";
import {isSkinOf, type GimmickName, type SkinOf} from "./gimmick-registry";
import {subscribeKeys} from "./storage-subscription";

/**
 * The look ONE viewer switched a gimmick tile to with its "Optik wechseln" button (feature plans 0071, 0072):
 * browser storage, per gimmick and tile, key `gimmicks.<gimmick>-skin.<tileId>` — for the stopwatch exactly the
 * key it used before the store was shared, so no choice is lost.
 *
 * Stored as `{skin, basedOn}`: the choice holds while the tile's saved look is still the one it was chosen over;
 * once someone changes the saved look in the dialog, the choice lapses and the new look shows. Every access is
 * guarded (`StorageHelper` catches broken JSON, not a throwing `getItem`/`setItem`).
 */
interface ViewerSkin {
    readonly skin: string;
    readonly basedOn: string;
}

function skinKey(gimmick: GimmickName, tileId: string): string {
    return `gimmicks.${gimmick}-skin.${tileId}`;
}

/** The look this viewer sees: their own choice while it still applies, otherwise the saved look (raw). */
export function loadViewerSkin(gimmick: GimmickName, tileId: string, savedSkin: string): string {
    try {
        const raw = StorageHelper.get<Partial<ViewerSkin>>(skinKey(gimmick, tileId));
        if (raw && isSkinOf(gimmick, raw.skin) && raw.basedOn === savedSkin) {
            return raw.skin;
        }
    } catch (error) {
        console.warn('The gimmick look could not be read', error);
    }
    return savedSkin;
}

export function saveViewerSkin<N extends GimmickName>(gimmick: N, tileId: string, skin: SkinOf<N>, savedSkin: string): void {
    try {
        StorageHelper.set<ViewerSkin>(skinKey(gimmick, tileId), {skin, basedOn: savedSkin});
    } catch (error) {
        console.warn('The gimmick look could not be stored', error);
    }
}

/** `onChange` when another tab changed this tile's look. */
export function subscribeViewerSkin(gimmick: GimmickName, tileId: string, onChange: () => void): () => void {
    return subscribeKeys([skinKey(gimmick, tileId)], onChange);
}
